import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, User, CheckCircle, XCircle, Clock, FileText, Loader2 } from 'lucide-react';
import logger from '@/lib/logger';

const typeColor = {
    annual_leave: 'bg-blue-500/10 text-blue-600',
    annual: 'bg-blue-500/10 text-blue-600',
    sick_leave: 'bg-orange-500/10 text-orange-600',
    sick: 'bg-orange-500/10 text-orange-600',
    half_day: 'bg-teal-500/10 text-teal-600',
    unpaid_leave: 'bg-red-500/10 text-red-600',
    unpaid: 'bg-red-500/10 text-red-600',
    umrah_leave: 'bg-purple-500/10 text-purple-600',
    umrah: 'bg-purple-500/10 text-purple-600',
    maternity_leave: 'bg-pink-500/10 text-pink-600',
    maternity: 'bg-pink-500/10 text-pink-600',
};

const fmtType = (t) => (t || 'leave').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Normalises both ESS and HR leave-request shapes into one view-model
function normalise(req, source) {
    if (source === 'ess') {
        // Derive the current pending level from status (pending_manager/hr/ceo)
        const lvl = (req.status || '').startsWith('pending_')
            ? req.status.replace('pending_', '')
            : (req.current_level || req.status);
        return {
            id: req.id,
            source: 'ess',
            employee_name: req.employee_name || 'Employee',
            leave_type: req.leave_type,
            start_date: req.start_date,
            end_date: req.end_date,
            total_days: req.total_days,
            reason: req.reason,
            current_level: lvl,
            status: req.status,
            can_act: req.can_act !== false,
            created_at: req.created_at,
            approval_chain: req.approval_chain,
            raw: req,
        };
    }
    return {
        id: req.id,
        source: 'hr',
        employee_name: req.employee_name || 'Employee',
        leave_type: req.leave_type,
        start_date: req.start_date,
        end_date: req.end_date,
        total_days: req.leave_days || req.total_days,
        reason: req.reason,
        current_level: req.current_approver_level,
        status: req.status,
        can_act: true,
        created_at: req.created_at,
        approval_chain: req.approval_chain,
        raw: req,
    };
}

export default function LeaveApprovalsPage() {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [active, setActive] = useState(null);   // selected leave for the detail dialog
    const [comments, setComments] = useState('');

    const fetchPending = useCallback(async () => {
        setLoading(true);
        try {
            // Both legacy ESS and the newer HR-flow are in play — merge them.
            const [essRes, hrRes] = await Promise.all([
                api.get('/ess/pending-approvals').catch(() => ({ data: {} })),
                api.get('/hr/leave-requests?pending_approval=true').catch(() => ({ data: [] })),
            ]);
            const essItems = (essRes.data?.leave_requests || []).map(r => normalise(r, 'ess'));
            const hrItems = (Array.isArray(hrRes.data) ? hrRes.data : []).map(r => normalise(r, 'hr'));

            // De-dup by id (in case the same record exists in both — defensive)
            const seen = new Set();
            const merged = [...essItems, ...hrItems].filter(r => {
                const k = `${r.source}:${r.id}`;
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
            });
            merged.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            setPending(merged);
        } catch (e) {
            logger.error('fetchPending failed', e);
            setPending([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPending(); }, [fetchPending]);

    const openDetails = (req) => {
        setActive(req);
        setComments('');
    };

    const handleAction = async (req, action) => {
        const id = req.id;
        const isApprove = action === 'approve';
        setProcessingId(id);
        try {
            if (req.source === 'ess') {
                await api.post(`/ess/leave-requests/${id}/action`, {
                    action,
                    comments,
                });
            } else {
                // HR flow uses PUT /hr/leave-requests/:id/approve with action body
                await api.put(`/hr/leave-requests/${id}/approve`, {
                    action,
                    comments,
                });
            }
            toast.success(`Leave ${isApprove ? 'approved' : 'rejected'}`);
            setActive(null);
            await fetchPending();
        } catch (err) {
            toast.error(err?.response?.data?.detail || `Failed to ${action} the request`);
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="leave-approvals-page">
            <div>
                <h1 className="text-2xl font-bold">Leave Approvals</h1>
                <p className="text-muted-foreground text-sm mt-1">Review pending leave requests · click a card for full details</p>
            </div>

            <Badge variant="outline" data-testid="leave-pending-count">{pending.length} pending approval(s)</Badge>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : pending.length === 0 ? (
                <Card>
                    <CardContent className="py-16 text-center">
                        <Calendar className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
                        <h3 className="text-lg font-semibold mb-1">All Clear</h3>
                        <p className="text-muted-foreground text-sm">No pending leave requests to review.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {pending.map((req) => (
                        <Card
                            key={`${req.source}-${req.id}`}
                            className="cursor-pointer hover:border-primary/40 hover:shadow-sm transition-all"
                            onClick={() => openDetails(req)}
                            data-testid={`leave-approval-card-${req.id}`}
                        >
                            <CardContent className="py-4 space-y-3">
                                <div className="flex items-center gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <User className="h-5 w-5 text-primary" />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium truncate">{req.employee_name}</p>
                                            {req.current_level && (
                                                <Badge variant="secondary" className="text-[10px]">
                                                    Awaiting {req.current_level.replace(/_/g, ' ')}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                                            <Badge className={typeColor[req.leave_type] || 'bg-muted'}>{fmtType(req.leave_type)}</Badge>
                                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{req.start_date || 'N/A'} → {req.end_date || 'N/A'}</span>
                                            <span>{req.total_days || 1} day(s)</span>
                                        </div>
                                        {req.reason && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{req.reason}</p>}
                                    </div>
                            <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 border-green-600/30 hover:bg-green-600/10"
                                    disabled={processingId === req.id}
                                    onClick={(e) => { e.stopPropagation(); openDetails(req); }}
                                    data-testid={`open-leave-${req.id}`}
                                >
                                    {req.can_act ? 'Review' : 'View'}
                                </Button>
                            </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Detail dialog */}
            <Dialog open={!!active} onOpenChange={(o) => { if (!o && !processingId) setActive(null); }}>
                <DialogContent className="max-w-lg" data-testid="leave-approval-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-primary" />
                            Leave request
                        </DialogTitle>
                        <DialogDescription>
                            Review the request and approve or reject.
                        </DialogDescription>
                    </DialogHeader>
                    {active && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted-foreground">Employee</div>
                                    <div className="font-medium">{active.employee_name}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">Type</div>
                                    <Badge className={typeColor[active.leave_type] || 'bg-muted'}>{fmtType(active.leave_type)}</Badge>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">From</div>
                                    <div>{active.start_date || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">To</div>
                                    <div>{active.end_date || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">Duration</div>
                                    <div>{active.total_days || 1} day(s)</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">Awaiting</div>
                                    <div className="capitalize">{(active.current_level || '').replace(/_/g, ' ') || 'approval'}</div>
                                </div>
                            </div>
                            {active.reason && (
                                <div>
                                    <div className="text-xs text-muted-foreground mb-1">Reason</div>
                                    <div className="text-sm rounded-md border bg-muted/30 p-3">{active.reason}</div>
                                </div>
                            )}
                            {Array.isArray(active.approval_chain) && active.approval_chain.length > 0 && (
                                <div>
                                    <div className="text-xs text-muted-foreground mb-1">Approval chain</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {active.approval_chain.map((step, i) => {
                                            const label = typeof step === 'string' ? step : (step.level || JSON.stringify(step));
                                            const isCurrent = (typeof step === 'string' ? step : step.level) === active.current_level;
                                            return (
                                                <Badge
                                                    key={i}
                                                    variant={isCurrent ? 'default' : 'outline'}
                                                    className="capitalize text-[10px]"
                                                >
                                                    {label.replace(/_/g, ' ')}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className="text-xs text-muted-foreground mb-1">Comments (optional)</div>
                                <Textarea
                                    placeholder="Add a note for the employee"
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    rows={3}
                                    data-testid="leave-comments-input"
                                />
                            </div>
                            <div className="flex items-center justify-end gap-2 pt-1">
                                <Button
                                    variant="outline"
                                    onClick={() => setActive(null)}
                                    disabled={!!processingId}
                                >
                                    Close
                                </Button>
                                {active.can_act ? (
                                    <>
                                        <Button
                                            variant="outline"
                                            className="text-red-600 border-red-600/30 hover:bg-red-600/10"
                                            onClick={() => handleAction(active, 'reject')}
                                            disabled={!!processingId}
                                            data-testid="leave-reject-btn"
                                        >
                                            {processingId === active.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
                                            Reject
                                        </Button>
                                        <Button
                                            className="bg-green-600 hover:bg-green-700 text-white"
                                            onClick={() => handleAction(active, 'approve')}
                                            disabled={!!processingId}
                                            data-testid="leave-approve-btn"
                                        >
                                            {processingId === active.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                                            Approve
                                        </Button>
                                    </>
                                ) : (
                                    <span className="text-xs text-muted-foreground italic" data-testid="leave-waiting-other-level">
                                        Waiting for {(active.current_level || 'approver').replace(/_/g, ' ')} to act first.
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
